import { Battery, Zap, Thermometer, Clock } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

interface FeaturesAndSpecsProps {
  product: any;
}

export default function FeaturesAndSpecs({ product }: FeaturesAndSpecsProps) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section ref={ref} id="features-specs" className="py-6 bg-white">
      <div
        className={`max-w-7xl mx-auto px-6 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl font-light text-gray-900 mb-4">
            Features & Specifications
          </h2>
          <div className="w-24 h-0.5 bg-sky-400 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-8 gap-24">
          <div className="lg:col-span-5 space-y-6">
            <h3 className="text-2xl font-semibold text-gray-900 items-center text-center mb-6">
              Key Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {product.features.map((feature: any, index: number) => (
                <div
                  key={index}
                  className="group bg-white p-4 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-500 border-l-4 border-sky-500 hover:border-sky-500/50"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isVisible
                      ? `fadeInLeft 0.6s ease-out ${index * 100}ms both`
                      : "none",
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="w-6 h-6 text-sky-500" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <h3 className="text-2xl font-semibold text-gray-900 items-center text-center mb-6">
              Technical Specifications
            </h3>
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
              <div className="space-y-5">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl group hover:bg-sky-50 transition-colors duration-300 border-l-4 border-sky-500 hover:border-sky-500/50">
                  <div className="flex items-center">
                    <Battery className="w-6 h-6 text-sky-500 mr-3" />
                    <span className="text-gray-900 font-semibold text-lg">
                      Capacity
                    </span>
                  </div>
                  <span className="text-gray-900 font-semibold text-sm group-hover:text-sky-600 transition-colors">
                    {product.specs.capacity}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl group hover:bg-sky-50 transition-colors duration-300 border-l-4 border-sky-500 hover:border-sky-500/50">
                  <div className="flex items-center">
                    <Zap className="w-6 h-6 text-sky-500 mr-3" />
                    <span className="text-gray-900 font-semibold text-lg">
                      Voltage
                    </span>
                  </div>
                  <span className="text-gray-900 font-semibold text-sm group-hover:text-sky-600 transition-colors">
                    {product.specs.voltage}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl group hover:bg-sky-50 transition-colors duration-300 border-l-4 border-sky-500 hover:border-sky-500/50">
                  <div className="flex items-center">
                    <Thermometer className="w-6 h-6 text-sky-500 mr-3" />
                    <span className="text-gray-900 font-semibold text-lg">
                      Temperature
                    </span>
                  </div>
                  <span className="text-gray-900 font-semibold text-sm group-hover:text-sky-600 transition-colors">
                    {product.specs.temperature}
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl group hover:bg-sky-50 transition-colors duration-300 border-l-4 border-sky-500 hover:border-sky-500/50">
                  <div className="flex items-center">
                    <Clock className="w-6 h-6 text-sky-500 mr-3" />
                    <span className="text-gray-900 font-semibold text-lg">
                      Cycle Life
                    </span>
                  </div>
                  <span className="text-gray-900 font-semibold text-sm group-hover:text-sky-600 transition-colors">
                    {product.specs.cycleLife}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
